import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createCloudRunAxios } from '../../../api/axios';

const remiApi = createCloudRunAxios('remi-api');

export interface IntegrationSetupRequest {
  id: string;
  flowId: string;
  flowName: string;
  orgId: string;
  orgName: string;
  contactType: 'buyer' | 'seller' | 'both' | '';
  crmSources: string[];
  useTags: boolean;
  tagsAction: 'include' | 'exclude' | '';
  tags: string[];
  useAssignment: boolean;
  assignmentAction: 'include' | 'exclude' | '';
  assignedAgents: string[];
  submittedBy: string;
  submittedAt: string;
  status: 'pending' | 'processing' | 'complete';
  createdAt: unknown;
  [key: string]: unknown;
}

interface ConfigData {
  flowId?: string;
  flowName?: string;
  orgId?: string;
  orgName?: string;
  contactType?: string;
  crmSources?: string[];
  useTags?: boolean;
  tagsAction?: string;
  tags?: string[];
  useAssignment?: boolean;
  assignmentAction?: string;
  assignedAgents?: string[];
  submittedBy?: string;
  submittedAt?: string;
  [key: string]: unknown;
}

interface RawIntegrationSetupRequest {
  request_id?: string;
  id?: string;
  organization_id?: string;
  integration_name?: string;
  status?: 'pending' | 'processing' | 'complete';
  created_at?: unknown;
  config_data?: ConfigData;
  [key: string]: unknown;
}

function mapRequest(raw: RawIntegrationSetupRequest): IntegrationSetupRequest {
  const c = raw.config_data ?? {};
  return {
    id: (raw.request_id ?? raw.id ?? '') as string,
    flowId: c.flowId ?? '',
    flowName: c.flowName ?? raw.integration_name ?? '',
    orgId: (raw.organization_id as string) ?? c.orgId ?? '',
    orgName: c.orgName ?? '',
    contactType: (c.contactType as IntegrationSetupRequest['contactType']) ?? '',
    crmSources: c.crmSources ?? [],
    useTags: c.useTags ?? false,
    tagsAction: (c.tagsAction as IntegrationSetupRequest['tagsAction']) ?? '',
    tags: c.tags ?? [],
    useAssignment: c.useAssignment ?? false,
    assignmentAction: (c.assignmentAction as IntegrationSetupRequest['assignmentAction']) ?? '',
    assignedAgents: c.assignedAgents ?? [],
    submittedBy: c.submittedBy ?? '',
    submittedAt: c.submittedAt ?? '',
    status: raw.status ?? 'pending',
    createdAt: raw.created_at,
  };
}

export const useIntegrationSetupRequests = (orgId: string | undefined) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['integration-setup-requests', orgId],
    queryFn: async () => {
      const response = await remiApi.get<{ items: RawIntegrationSetupRequest[] } | RawIntegrationSetupRequest[]>(
        `/api/v1/organizations/${orgId}/integration-setup-requests`
      );
      const items = Array.isArray(response.data)
        ? response.data
        : (response.data as { items: RawIntegrationSetupRequest[] }).items ?? [];
      return items.map(mapRequest);
    },
    enabled: !!orgId,
    staleTime: Infinity,
  });

  return {
    data: data ?? ([] as IntegrationSetupRequest[]),
    isLoading,
    error: error as Error | null,
    refetch,
  };
};

export const useUpdateIntegrationSetupStatus = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: async ({ orgId, requestId, newStatus }: { orgId: string; requestId: string; newStatus: string; flowId?: string }) => {
      const response = await remiApi.patch(
        `/api/v1/organizations/${orgId}/integration-setup-requests/${requestId}`,
        { status: newStatus }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integration-setup-requests', variables.orgId] });
    },
  });

  return {
    mutate,
    mutateAsync,
    isPending,
    isError,
    error: error as Error | null,
  };
};
